// ============================================================================
// Delivery Control Tower API — self-contained axios calls, mirroring
// src/pages/oms/logistics/logistics.api.ts's conventions (the app's default axios
// instance already carries baseURL + JWT — relative paths inherit auth).
//
// Backend: oms/api/views/control_tower.py (ControlTowerSummaryView / ControlTowerShipmentsView),
// built on oms/logistics/services/control_tower.py. SLA is not computed there yet — every row's
// `sla_state` comes back `null`, and the summary carries no SLA count at all.
// ============================================================================
import { message } from "antd";
import axios from "axios";
import { FirstMileWarehouseKey, LastMileLaneKey } from "./types";

// Which half of the board to scope to — the same first/last-mile split LogisticsOrderListView
// uses (see oms/types.ts BoardLane). Defaults to last_mile server-side when omitted.
export type ControlTowerBoardLane = "first_mile" | "last_mile";

export interface ControlTowerScopeParams {
  // The window, under the name the ACTIVE board scopes on: last_mile reads delivery_*, first_mile
  // reads dispatch_* (the vendor's committed availability). Each bound defaults server-side to
  // TODAY when omitted — so a first-mile call that sends only delivery_* silently narrows to today
  // and comes back empty. Callers that can run on either board send BOTH pairs.
  delivery_from?: string; // YYYY-MM-DD, inclusive
  delivery_to?: string;
  dispatch_from?: string;
  dispatch_to?: string;
  assigned_to?: string; // the ops owner's user id (same value listTeamMembers' dropdown uses)
  board_lane?: ControlTowerBoardLane; // first_mile / last_mile; default last_mile
}

export interface ControlTowerLaneCounts {
  total_orders: number;
  total_shipments: number;
  total_at_risk: number; // rows in this lane about to miss SLA (sla_state === "at_risk")
  total_breached: number; // rows in this lane that already missed SLA (sla_state === "breached")
  // Reason-slug → count breakdowns behind the two SLA figures (zero buckets included, e.g.
  // {"vehicle_not_booked": 9, ...}) — shown on hovering the At Risk / Breached numbers.
  at_risk_reasons?: Record<string, number> | null;
  breach_reasons?: Record<string, number> | null;
}

// The backend's 8 lanes — the 7 the board renders plus "unclassified" (bad/unresolvable pincode
// data), which the UI surfaces as a caveat rather than a card of its own.
export type ControlTowerLaneKey = LastMileLaneKey | "unclassified";

// Fulfilment stage of a row, sent by the backend on both orders and shipments — drives the
// drill-down drawer's status tabs.
export type ControlTowerStatus = "needs_booking" | "booked_but_not_picked" | "picked" | "delivered";

// Per-status totals for the whole (unpaginated) result set — the backend computes these so the
// drawer's tab badges stay accurate even though the table itself is paginated server-side.
export type ControlTowerStatusCounts = Partial<Record<ControlTowerStatus, number>>;

// SLA state per row. "breached" = SLA already missed, "at_risk" = about to miss, "on_time" = safe.
// null = not yet evaluable for this row.
export type ControlTowerSlaState = "breached" | "at_risk" | "on_time" | null;

// Page controls for the drill-down lists (orders/shipments). The summary endpoint doesn't paginate.
export interface ControlTowerPageParams {
  page?: number; // 1-based
  page_size?: number;
}

export interface ControlTowerSummaryResponse {
  lanes: Record<ControlTowerLaneKey, ControlTowerLaneCounts>;
  total_shipments: number;
  total_orders: number;
  total_at_risk: number; // about to miss SLA, across all lanes
  total_breached: number; // already missed SLA, across all lanes
  // Board-wide reason-slug → count breakdowns behind the two SLA totals — hover tooltips on
  // the overview strip's At Risk / Breached cards.
  at_risk_reasons?: Record<string, number> | null;
  breach_reasons?: Record<string, number> | null;
}

export async function getControlTowerSummary(
  params: ControlTowerScopeParams
): Promise<ControlTowerSummaryResponse | undefined> {
  try {
    const res = await axios.get("/oms/control-tower/summary/", { params });
    return res?.data as ControlTowerSummaryResponse;
  } catch {
    return undefined;
  }
}

// First Mile summary — the same endpoint as the last-mile summary with board_lane=first_mile.
// The First Mile tab reads only the transfer board off it; the GRN and Pick List sections have
// their own escalation endpoints below.
export interface FirstMileSummaryResponse {
  board_lane: "first_mile";
  // Inter-warehouse stock transfers on the Bengaluru↔Hyderabad line-haul. Optional — a backend
  // that doesn't send it simply shows no Transfer Orders board (the section renders nothing).
  transfer_orders?: TransferOrdersSummary;
}

// --- Transfer Orders (inter-warehouse line-haul) -----------------------------------------------
// One Delhivery/partner transfer between MD warehouses, on the Bengaluru↔Hyderabad line-haul. The
// summary breaks these down by direction, then by the RECEIVING warehouse; the drill-down lists the
// legs behind a count via /first-mile-receives/?kind=transfer.

// Exactly two directions. blr_to_hyd only ever lands at hyderabad; hyd_to_blr lands at one of the
// three Bengaluru docks. The key set is stable — the UI renders zero cards rather than hiding them.
export type TransferDirection = "blr_to_hyd" | "hyd_to_blr";

// The receiving warehouse a transfer lands at. Distinct from FirstMileWarehouseKey: transfers split
// Horamavu and Warehouse Now into separate docks (they share a card elsewhere).
export type TransferDockKey = "hyderabad" | "horamavu" | "rebound" | "whnow";

// The three LIFECYCLE states of a transfer — physical progress. yet_to_pick = not yet dispatched
// from source; in_transit = dispatched, moving; transferred = received at destination. These three
// (and only these) sum to total_shipments.
export type TransferStatusKey = "yet_to_pick" | "in_transit" | "transferred";

// One receiving-warehouse cell. The three lifecycle counts sum to total_shipments. `error` is an
// OVERLAY, not a fourth status: a stuck leg is counted in BOTH its lifecycle bucket and here, so it
// must never be added into the lifecycle bar. `total_cargo_weight_kg` is the cell's total weight.
export interface TransferOrderCounts {
  total_shipments: number;
  yet_to_pick: number;
  in_transit: number;
  transferred: number;
  error: number; // overlay count — stuck legs; also counted in their lifecycle bucket
  total_quantity: number | string; // the dock's aggregate transfer quantity
  total_cargo_weight_kg: number | string; // number, or a Django-serialised decimal string
}

// The summary's transfer_orders block: each direction maps its (stable) receiving warehouses to a
// counts cell. A warehouse may be absent when it has no rows in scope — the UI fills a zero cell.
export interface TransferOrdersSummary {
  blr_to_hyd: Partial<Record<TransferDockKey, TransferOrderCounts>>;
  hyd_to_blr: Partial<Record<TransferDockKey, TransferOrderCounts>>;
}

// First Mile scopes by dispatch date (when stock leaves the vendor), not delivery date — so it
// takes dispatch_from / dispatch_to rather than ControlTowerScopeParams' delivery_from/_to.
export interface FirstMileScopeParams {
  dispatch_from?: string; // YYYY-MM-DD, inclusive
  dispatch_to?: string;
  assigned_to?: string;
}

// Same endpoint as getControlTowerSummary, but board_lane=first_mile returns the nested
// first-mile breakdown above rather than the last-mile lane totals.
export async function getFirstMileSummary(
  params: FirstMileScopeParams
): Promise<FirstMileSummaryResponse | undefined> {
  try {
    const res = await axios.get("/oms/control-tower/summary/", {
      params: { ...params, board_lane: "first_mile" },
    });
    return res?.data as FirstMileSummaryResponse;
  } catch {
    return undefined;
  }
}

// --- Team Metric: per-owner performance on the active board -------------------------------------
// GET /control-tower/team-metrics/ — one row per person with work in scope, on the SAME scope the
// summary uses (board_lane + that lane's date window), so a member's total_assigned equals the
// summary's Total Shipments filtered to them. Backend: ControlTowerTeamMetricsView.
export interface TeamEscalationReason {
  reason: string; // the drawer's "What's the problem?" answer (issue_type)
  count: number;
}

export interface TeamMetricMember {
  user_id: string;
  name: string;
  email: string | null;
  total_assigned: number; // bookings they own in scope
  // Per-shipment SLA. NULL (not 0) on first_mile — that SLA rule isn't defined for this board yet,
  // so the card renders an em dash rather than claiming nobody is late.
  breached: number | null;
  at_risk: number | null;
  undelivered: number; // owned bookings not yet in the `delivered` bucket (booking-level, not GRN)
  // FIRST MILE ONLY (null on last mile): of their owned inbounds, how many have the GRN filed.
  // Deliberately not the inverse of `undelivered` — a truck reaches our dock first, GRN comes after.
  grn_marked: number | null;
  escalations_raised: number; // issue_raised events THEY authored on this lane
  top_escalation_reasons: TeamEscalationReason[]; // their 3 most common, already sorted
}

export interface TeamMetricsResponse {
  board_lane: ControlTowerBoardLane;
  members: TeamMetricMember[]; // busiest owner first; empty when nobody worked this lane in scope
}

export interface TeamMetricsParams {
  board_lane: ControlTowerBoardLane;
  // The same comma-separated roles the "Assigned to" dropdown is built from, so the table lists
  // exactly the people that dropdown offers. Omit for every owner, whatever their role.
  role?: string;
  delivery_from?: string; // last_mile window
  delivery_to?: string;
  dispatch_from?: string; // first_mile window
  dispatch_to?: string;
  assigned_to?: string; // narrows to one person, same as the rest of the board
}

export async function getTeamMetrics(
  params: TeamMetricsParams
): Promise<TeamMetricsResponse | undefined> {
  try {
    const res = await axios.get("/oms/control-tower/team-metrics/", { params });
    return res?.data as TeamMetricsResponse;
  } catch {
    return undefined;
  }
}

// --- Team Metric drill-down: the escalations behind one person's count ---------------------------
// GET /control-tower/team-metrics/escalations/ — the `issue_raised` events THAT person authored on
// this lane, same scope as the team-metrics call, so `count` equals the clicked tally. `reason`
// narrows to one issue_type (what clicking a row of the top-3 list opens).
// Backend: ControlTowerTeamEscalationsView.
export interface TeamEscalationRow {
  id: number; // the operation event id
  stage_id: number;
  issue_type: string; // the drawer's "What's the problem?" answer
  category: string | null; // which team's queue it routed to
  remarks: string;
  raised_at: string | null; // ISO
  resolved: boolean; // a later issue_resolved landed on the same stage
  logistics_document_id: number | null; // null for a GRN / pick queue row (books no truck)
  po_number: string | null;
  lead_id: string | null;
  oms_sales_order_id: number | null;
  express: boolean;
  leg: "booking" | "receive" | "pick";
}

export interface TeamEscalationsResponse {
  count: number;
  results: TeamEscalationRow[];
  page?: number;
  page_size?: number;
  num_pages?: number;
  next?: number | null;
  previous?: number | null;
}

export interface TeamEscalationsParams {
  user_id: string; // required server-side — this is one person's list
  board_lane: ControlTowerBoardLane;
  delivery_from?: string;
  delivery_to?: string;
  dispatch_from?: string;
  dispatch_to?: string;
  reason?: string; // one issue_type
  page?: number;
  page_size?: number;
}

export async function listTeamEscalations(
  params: TeamEscalationsParams
): Promise<TeamEscalationsResponse | undefined> {
  try {
    const res = await axios.get("/oms/control-tower/team-metrics/escalations/", { params });
    return res?.data as TeamEscalationsResponse;
  } catch {
    return undefined;
  }
}

// --- First Mile drill-down: the pick legs behind the Pick List section's picked tally ---------
// GET /first-mile-receives/?kind=picklist — one row per PICK leg (OperationStage), keyed by the
// warehouse picked FROM. The receive-leg variant (kind=receive) was removed with the First Mile
// Overview cards it backed; receive legs are listed per GRN bucket by listFirstMileEscalationRows.
// kind=receive returns the inbound RECEIVE legs instead, with a GRN vocabulary in place of the
// pick one. Every other field is shared with FirstMilePickRow, so the drawer's columns are common.
export interface FirstMileReceiveRow {
  id: number;
  po_number: string | null;
  express: boolean;
  unloading: boolean;
  lead_id: string | null;
  estimate_status: string;
  supplier: string | null;
  dock: FirstMileWarehouseKey; // receiving warehouse
  target_delivery_date: string | null;
  due_at: string | null;
  leg_status: string | null;
  issue_state: string | null;
  grn_marked: boolean; // true once the GRN is filed
  yet_to_receive: boolean; // = !grn_marked
  error_message: string | null;
  hold_reason: string | null;
  sla_state: ControlTowerSlaState;
}

export interface FirstMilePickRow {
  id: number; // pick leg (OperationStage) id
  po_number: string | null;
  express: boolean; // shown as an "Express" pill beside the PO
  unloading: boolean; // shown as an "Unloading" pill beside the PO
  lead_id: string | null;
  estimate_status: string;
  supplier: string | null;
  dock: FirstMileWarehouseKey; // warehouse picked from
  warehouse_id: number | null;
  target_delivery_date: string | null;
  due_at: string | null;
  leg_status: string | null;
  issue_state: string | null;
  picked: boolean; // issue_state === "completed"
  yet_to_pick: boolean;
  error_message: string | null;
  hold_reason: string | null;
  sla_state: ControlTowerSlaState;
}

export type FirstMileDrilldownRow = FirstMilePickRow | FirstMileReceiveRow;

export interface FirstMileReceivesResponse {
  count: number; // equals the clicked summary tally under matching filters
  results: FirstMileDrilldownRow[];
  // Only present when pagination is opted into (page or page_size sent):
  page?: number;
  page_size?: number;
  num_pages?: number;
  next?: number | null;
  previous?: number | null;
}

export interface FirstMileReceivesParams {
  kind: "picklist" | "receive";
  dispatch_from?: string; // pass the same window as the summary
  dispatch_to?: string;
  dock?: FirstMileWarehouseKey;
  assigned_to?: string; // narrow to one owner's inbound bookings
  yet_to_pick?: boolean; // picklist: true = only not yet picked
  yet_to_receive?: boolean; // receive: false = only GRN-marked legs
  page?: number; // opt-in pagination — omit both to get the full list
  page_size?: number; // max 200
}

export async function listFirstMileReceives(
  params: FirstMileReceivesParams
): Promise<FirstMileReceivesResponse | undefined> {
  try {
    const res = await axios.get("/oms/control-tower/first-mile-receives/", { params });
    return res?.data as FirstMileReceivesResponse;
  } catch {
    return undefined;
  }
}

// --- Transfer Orders drill-down: the legs behind a transfer count -------------------------------
// One cargo line on a transfer leg — a variant, its quantity, and the weight it contributes.
export interface TransferCargoLine {
  variant_id: number;
  sku: string | null;
  name: string;
  qty: number;
  unit_weight_kg: number | null; // per-unit weight; null when the variant has no density mapping
  weight_kg: number | null; // qty × unit_weight_kg (server-computed); null when unmapped
}

// Same endpoint as the receive/pick drill-down, kind=transfer. One row per transfer leg.
export interface TransferOrderRow {
  id: number;
  oms_sales_order_id: number | null; // Lead ID links to the OMS order when present
  po_number: string | null;
  lead_id: string | null;
  estimate_status: string; // feed through getPIOrderStatusLabel
  supplier: string | null;
  express: boolean; // shown as an "Express" pill beside the PO
  unloading: boolean; // shown as an "Unloading" pill beside the PO
  direction: TransferDirection;
  dock: TransferDockKey; // receiving/destination warehouse (constant within a dock drill-down)
  // The SOURCE warehouse — the distinguishing column in a drill-down, since the dock is constant.
  // Resolved dynamically (never a fixed list); fall back to the id, then "—" when null.
  origin_warehouse: string | null;
  origin_warehouse_id: number | null;
  source_warehouse_id: number | null;
  receiving_warehouse_id: number | null;
  target_delivery_date: string | null; // YYYY-MM-DD
  due_at: string | null; // ISO timestamp
  // Aging inputs for a stuck transfer — when the customer ordered, and when this transfer's
  // allocation was created. Both ISO datetimes, either may be null.
  order_placed_time: string | null;
  allocation_created_time: string | null;
  leg_status: string | null; // current state of this transfer leg
  issue_state: string | null;
  status: TransferStatusKey; // LIFECYCLE state (yet_to_pick/in_transit/transferred) — the status chip
  yet_to_pick: boolean;
  in_transit: boolean;
  transferred: boolean;
  // Whether the goods were received (GRN/picklist marked) at the SOURCE warehouse. false = still
  // blocked upstream — shown as an "Awaiting GRN at source" chip (use this, not error parsing).
  previous_warehouse_grn_marked: boolean;
  // Error is INDEPENDENT of status: a stuck leg keeps its lifecycle chip and gets a separate warning
  // badge. `has_error` drives the badge; `error` is the short label, with detail in the two below.
  has_error: boolean;
  error: string | null;
  error_message: string | null; // longer detail behind the error badge
  hold_reason: string | null; // legitimately-waiting detail, also shown behind the badge
  sla_state: ControlTowerSlaState; // always null for now — no SLA column rendered
  // Cargo: the leg's quantity, total weight and its per-line breakdown (expandable in the table).
  // Weight typed loosely — a Django DecimalField serialises as a string ("52.92"), not a number.
  total_quantity?: number | string | null;
  cargo_weight_kg?: number | string | null;
  cargo_lines?: TransferCargoLine[];
}

// The drill-down is GROUPED BY LEAD (order): one group per lead, holding its transfer legs. A lead's
// legs never split across a page — pagination is by group.
export interface TransferOrderGroup {
  lead_id: string | null;
  oms_sales_order_id: number | null; // links to the OMS order when present
  transfer_count: number; // legs in this lead (a split ships as several)
  total_quantity: number | string; // group aggregate
  total_cargo_weight_kg: number | string; // group aggregate
  has_error: boolean; // any leg in the group is stuck
  transfers: TransferOrderRow[]; // the leg rows under this lead
}

export interface TransferOrdersResponse {
  count: number; // number of LEADS (groups) — the pagination unit
  transfer_count: number; // total transfer legs across all groups == the card's total_shipments
  results: TransferOrderGroup[];
  // Grand totals across the WHOLE filtered set (all pages), when the backend sends them — shown at
  // the top of the drawer. Optional: the drawer falls back to summing the loaded groups otherwise.
  total_quantity?: number | string | null;
  total_cargo_weight_kg?: number | string | null;
  // Only present when pagination is opted into (page or page_size sent):
  page?: number;
  page_size?: number;
  num_pages?: number;
  next?: number | null;
  previous?: number | null;
}

export interface TransferOrdersParams {
  dispatch_from?: string; // pass the same window as the summary
  dispatch_to?: string;
  assigned_to?: string; // same owner scope as the summary
  direction?: TransferDirection;
  dock?: TransferDockKey; // the receiving warehouse
  status?: TransferStatusKey; // a lifecycle bucket
  has_error?: boolean; // clicking a card's error count sends has_error=true (independent of status)
  page?: number; // opt-in pagination — omit both to get the full list
  page_size?: number;
}

// An unrecognised direction / dock / status returns 400 {"detail": "..."} — surfaced as a toast
// (it won't happen from a normal card click, which only ever sends valid values).
export async function listTransferOrders(
  params: TransferOrdersParams
): Promise<TransferOrdersResponse | undefined> {
  try {
    const res = await axios.get("/oms/control-tower/first-mile-receives/", {
      params: { ...params, kind: "transfer" },
    });
    return res?.data as TransferOrdersResponse;
  } catch (error: any) {
    message.error(error?.response?.data?.detail || "Could not load transfer orders");
    return undefined;
  }
}

// --- First Mile GRN section: escalations + exclusions, per warehouse ---------------------------
// GET /first-mile-escalations/ — everything the GRN section needs, board-wide and per dock. Rooted
// on the RECEIVE LEG (the GRN), which is what makes the per-warehouse split possible and makes the
// weight match the logistics board's per-shipment weight. Backend:
// ControlTowerFirstMileEscalationsView (oms/api/views/control_tower.py).

// The three DERIVED exclusions — GRNs that can't fairly be held to the same-day SLA.
export type FirstMileExclusionKey = "po_after_6pm" | "heavy_po" | "large_tile";

// Everything drillable in the GRN section: the raised escalation, each exclusion, plus the
// aggregate "excluded" (any exclusion) and "all".
export type FirstMileEscalationBucketKey = "vendor_to_send" | FirstMileExclusionKey;
export type EscalationDrilldownKey = FirstMileEscalationBucketKey | "excluded" | "all";

// One warehouse's (or the whole board's) GRN tally. `excluded` is DISTINCT GRNs hit by at least one
// exclusion, so it is <= the sum of `exclusions`. `excluded_marked` is how many of those were marked
// anyway — together they give the SLA-adjusted completion rate:
//   raw      = marked / total
//   adjusted = (marked - excluded_marked) / (total - excluded)
export interface FirstMileGrnCell {
  total: number;
  marked: number;
  escalations: number;
  exclusions: Record<FirstMileExclusionKey, number>;
  excluded: number;
  excluded_marked: number;
}

export interface FirstMileEscalationsResponse {
  board_lane: "first_mile";
  totals: FirstMileGrnCell; // board-wide; includes escalations no warehouse could be resolved for
  by_warehouse: Record<FirstMileWarehouseKey, FirstMileGrnCell>;
}

// The GRN completion + exclusion figures honour the `assigned_to` owner filter (same root set as
// the summary, so the two always agree). The RAISED escalations are procurement-team-wide — they
// route to the procurement groups, not to a last-mile owner — so that one count ignores it.
export async function getFirstMileEscalations(
  params: { dispatch_from?: string; dispatch_to?: string; assigned_to?: string }
): Promise<FirstMileEscalationsResponse | undefined> {
  try {
    const res = await axios.get("/oms/control-tower/first-mile-escalations/", { params });
    return res?.data as FirstMileEscalationsResponse;
  } catch {
    return undefined;
  }
}

// One drill-down row. `kind="grn"` rows (the exclusions) carry the GRN's cargo weight and every
// reason it's excluded; `kind="escalation"` rows carry the raised issue instead.
export interface FirstMileEscalationRow {
  kind: "grn" | "escalation";
  id: number;
  po_number: string | null;
  lead_id: string | null;
  vendor: string | null;
  dock: FirstMileWarehouseKey | null;
  // kind === "grn"
  dispatch_date?: string | null; // YYYY-MM-DD
  weight_kg?: number;
  marked?: boolean;
  reasons?: FirstMileExclusionKey[];
  // kind === "escalation"
  issue_type?: string | null;
  remarks?: string | null;
  raised_at?: string | null;
}

export interface FirstMileEscalationRowsResponse {
  count: number;
  results: FirstMileEscalationRow[];
  page?: number;
  page_size?: number;
  num_pages?: number;
  next?: number | null;
  previous?: number | null;
}

export async function listFirstMileEscalationRows(
  // bucket "excluded" = every GRN hit by any exclusion, once, with all its reasons.
  // dock narrows to one warehouse (the table's row-level drill-down).
  params: {
    bucket?: EscalationDrilldownKey;
    dock?: FirstMileWarehouseKey;
    dispatch_from?: string;
    dispatch_to?: string;
    assigned_to?: string;
    page?: number;
    page_size?: number;
  }
): Promise<FirstMileEscalationRowsResponse | undefined> {
  try {
    const res = await axios.get("/oms/control-tower/first-mile-escalations/rows/", { params });
    return res?.data as FirstMileEscalationRowsResponse;
  } catch {
    return undefined;
  }
}

// --- First Mile picklist section: completion + the 30-min-SLA buckets, per warehouse ------------
// GET /first-mile-picklist-escalations/ — same per-dock shape as the GRN section. Both buckets are
// timed from PICK_REQUESTED (when the picklist was raised).
export type FirstMilePicklistEscalationBucketKey = "pending_over_30m" | "slow_pick_over_30m";
export type PicklistEscalationDrilldownKey = FirstMilePicklistEscalationBucketKey | "all";

// One warehouse's (or the board's) pick tally. The two buckets are disjoint, so
// `escalations === buckets.pending_over_30m + buckets.slow_pick_over_30m`.
export interface FirstMilePickCell {
  total: number;
  picked: number;
  escalations: number;
  buckets: Record<FirstMilePicklistEscalationBucketKey, number>;
}

export interface FirstMilePicklistEscalationsResponse {
  board_lane: "first_mile";
  sla_minutes: number; // 30 — the SLA the buckets flag against, for the UI labels
  totals: FirstMilePickCell;
  by_warehouse: Record<FirstMileWarehouseKey, FirstMilePickCell>;
}

// Honours the same `assigned_to` owner filter as the summary, so the picklist section's completion
// numbers never disagree with the pick cards further down the tab.
export async function getFirstMilePicklistEscalations(
  params: { dispatch_from?: string; dispatch_to?: string; assigned_to?: string }
): Promise<FirstMilePicklistEscalationsResponse | undefined> {
  try {
    const res = await axios.get("/oms/control-tower/first-mile-picklist-escalations/", { params });
    return res?.data as FirstMilePicklistEscalationsResponse;
  } catch {
    return undefined;
  }
}

// One drill-down row behind a picklist bucket. `minutes` = how long it waited (pending) or took (slow).
export interface FirstMilePicklistEscalationRow {
  kind: "pick";
  id: number;
  po_number: string | null;
  lead_id: string | null;
  vendor: string | null; // often null — picks are from an MD warehouse, not a vendor
  dock: FirstMileWarehouseKey;
  minutes: number | null;
  reason: FirstMilePicklistEscalationBucketKey;
}

export interface FirstMilePicklistEscalationRowsResponse {
  count: number;
  results: FirstMilePicklistEscalationRow[];
  page?: number;
  page_size?: number;
  num_pages?: number;
  next?: number | null;
  previous?: number | null;
}

export async function listFirstMilePicklistEscalationRows(
  params: {
    bucket?: PicklistEscalationDrilldownKey;
    dock?: FirstMileWarehouseKey;
    dispatch_from?: string;
    dispatch_to?: string;
    assigned_to?: string;
    page?: number;
    page_size?: number;
  }
): Promise<FirstMilePicklistEscalationRowsResponse | undefined> {
  try {
    const res = await axios.get("/oms/control-tower/first-mile-picklist-escalations/rows/", { params });
    return res?.data as FirstMilePicklistEscalationRowsResponse;
  } catch {
    return undefined;
  }
}

export interface ControlTowerShipmentRow {
  id: number;
  oms_sales_order_id: number | null; // Lead ID links to the OMS order page when present
  po_numbers: string[]; // every PO riding this shipment (consolidated onto one truck)
  express: boolean; // shown as an "Express" pill beside the PO numbers
  unloading: boolean; // shown as an "Unloading" pill beside the PO numbers
  lead_id: string | null;
  estimate_status: string; // Estimate.estimate_status — feed through getPIOrderStatusLabel
  delivery_date: string | null; // YYYY-MM-DD — Allocation.target_delivery_date
  shipment_status: string; // DeliveryStatus: created/booked/in_transit/attempted/delivered/cancelled
  assigned_to: string | null; // display name, already resolved server-side
  lane: ControlTowerLaneKey;
  board: ControlTowerStatus; // fulfilment stage — drives the drawer's status tabs
  sla_state: ControlTowerSlaState; // "breached" → "At Risk", "on_time" → "On Time"
  breach_reason: string | null; // why the SLA breached — shown on hovering the Breached pill
  risk_reason: string | null; // why the SLA is at risk — shown on hovering the At Risk pill
  // WHERE THE GOODS COME FROM — the "From" column. One rule covers the three cases ops care about:
  // a consolidated truck and our own stock both leave OUR warehouse and read as it; a single
  // dropship PO leaves the vendor's warehouse and reads as the vendor.
  origin_label: string | null;
  supplier: string | null; // vendor on the lead allocation; null for a stock movement
  dock: FirstMileWarehouseKey | null; // receiving warehouse — inbound legs only
  dispatch_date: string | null; // vendor's committed goods-ready date (the First Mile date axis)
  cargo_weight_kg: number | null; // same dimensioning the logistics board and GRN list use
  grn_marked: boolean | null; // inbound legs only; null on a last-mile row, which has no GRN
}

export interface ControlTowerShipmentsResponse {
  count: number; // total rows in scope (across all pages), for the pager
  board_counts: ControlTowerStatusCounts; // whole-set totals per board, for the tab badges
  results: ControlTowerShipmentRow[]; // just this page
}

export async function listControlTowerShipments(
  // lane omitted = all lanes in one response (each row still carries its own `lane`)
  // board omitted = all boards; when set, the backend filters and scopes `count` to it
  // sla_state omitted = all; the "At Risk" / "Breached" drill-downs send "at_risk" / "breached"
  params: ControlTowerScopeParams &
    ControlTowerPageParams & {
      lane?: ControlTowerLaneKey;
      board?: ControlTowerStatus | string; // comma-separated for multi-select, e.g. "picked,delivered"
      sla_state?: "breached" | "at_risk" | "on_time";
    }
): Promise<ControlTowerShipmentsResponse | undefined> {
  try {
    const res = await axios.get("/oms/control-tower/shipments/", { params });
    return res?.data as ControlTowerShipmentsResponse;
  } catch {
    return undefined;
  }
}

export interface ControlTowerOrderRow {
  lead_id: string | null;
  oms_sales_order_id: number | null; // Lead ID links to the OMS order page when present
  po_numbers: string[]; // every PO raised against this order; empty when none raised yet (or all cancelled)
  express: boolean; // shown as an "Express" pill beside the PO numbers
  unloading: boolean; // shown as an "Unloading" pill beside the PO numbers
  estimate_status: string; // Estimate.estimate_status — feed through getPIOrderStatusLabel
  po_status: string | null; // AllocationState (never "cancelled"); null when there's no PO
  error_message: string | null; // failure reason, sent only when po_status === "error"
  target_delivery_date: string | null; // YYYY-MM-DD; null when there's no PO
  assigned_to: string | null; // the shipment's Owner, once one's booked; null until then
  lane: ControlTowerLaneKey;
  board: ControlTowerStatus; // fulfilment stage — drives the drawer's status tabs
  sla_state: ControlTowerSlaState; // "breached" → "At Risk", "on_time" → "On Time"
  breach_reason: string | null; // why the SLA breached — shown on hovering the Breached pill
  risk_reason: string | null; // why the SLA is at risk — shown on hovering the At Risk pill
  // WHERE THE GOODS COME FROM — the "From" column. One rule covers the three cases ops care about:
  // a consolidated truck and our own stock both leave OUR warehouse and read as it; a single
  // dropship PO leaves the vendor's warehouse and reads as the vendor.
  origin_label: string | null;
  supplier: string | null; // vendor on the lead allocation; null for a stock movement
  dock: FirstMileWarehouseKey | null; // receiving warehouse — inbound legs only
  dispatch_date: string | null; // vendor's committed goods-ready date (the First Mile date axis)
  cargo_weight_kg: number | null; // same dimensioning the logistics board and GRN list use
  grn_marked: boolean | null; // inbound legs only; null on a last-mile row, which has no GRN
}

export interface ControlTowerOrdersResponse {
  count: number; // total rows in scope (across all pages), for the pager
  board_counts: ControlTowerStatusCounts; // whole-set totals per board, for the tab badges
  results: ControlTowerOrderRow[]; // just this page
}

export async function listControlTowerOrders(
  // lane omitted = all lanes in one response (each row still carries its own `lane`)
  // board omitted = all boards; when set, the backend filters and scopes `count` to it
  // sla_state omitted = all; accepted for symmetry with the shipments call
  params: ControlTowerScopeParams &
    ControlTowerPageParams & {
      lane?: ControlTowerLaneKey;
      board?: ControlTowerStatus | string; // comma-separated for multi-select, e.g. "picked,delivered"
      sla_state?: "breached" | "at_risk" | "on_time";
    }
): Promise<ControlTowerOrdersResponse | undefined> {
  try {
    const res = await axios.get("/oms/control-tower/orders/", { params });
    return res?.data as ControlTowerOrdersResponse;
  } catch {
    return undefined;
  }
}
